import { useDateFormatter } from '@react-aria/i18n';
import parseISO from 'date-fns/parseISO';
import { useCallback, useMemo } from 'react';
import { InfiniteData } from 'react-query';

export interface InputEventData {
  event_date: string;
  event_time: string;
  arena_name?: string;
  px_space?: string;
  session_type: string;
  ticket_offer_desc: string;
  offer_desc: string;
  arena_sort_no: string | number;
  ticket_offer_sort_no: string | number;
  price_from: string;
  avail_status: 'LOW' | 'LIMITED' | 'HIGH' | 'SELLING FAST' | 'ALLOCATION EXHAUSTED' | string;
  onsale_status: 'ONSALE' | string;
  button_text: string;
  buy_link: string;
  more_info_link?: string;
  image_link: string;
  event_blurb: string;
  icon?: string;
  table_availability?: '' | string[];
}

export type EventsResult = {
  data: InputEventData[];
  pagination: { next?: number | boolean; allDataLength?: number };
};

export type EventsDataFetchFn = (options: {
  showPastTickets?: boolean;
  page: number;
  pageSize?: number;
  endPoint?: string;
  filters?: {
    budget?: number;
    offerType?: string;
    ticketOffers?: React.Key[];
    sessions?: React.Key[];
    arenas?: React.Key[];
    date?: string[];
  };
  ticketOffersOption?: React.Key[];
}) => Promise<EventsResult>;

export const fetchEvents: EventsDataFetchFn = ({
  showPastTickets = false,
  page,
  pageSize,
  endPoint = 'https://ausopen-api-dev.f10.page/',
  filters: { ticketOffers, date, budget, offerType, ...filters } = {},
  ticketOffersOption = undefined
}) => {
  const getDate = (date: any) => {
    if (date) {
      if (Array.isArray(date)) {
        if (date?.length !== 0) {
          return date;
        } else {
          return undefined;
        }
      }
    }
    return date;
  };
  const date_ = getDate(date);
  const searchParams = new URLSearchParams(
    Object.entries({
      page,
      show_past_tickets: showPastTickets ? true : null,
      page_size: pageSize?.toString(),
      offer_category: offerType,
      ticket_offers: Boolean(ticketOffers) ? ticketOffers : ticketOffersOption,
      dates: date_,
      budget: budget === 10000 ? undefined : budget,
      ...filters
    })
      .filter(([key, value]) => value != null)
      .map(([key, value]) => [key, value!.toString()])
  );
  return fetch(endPoint + '?' + searchParams.toString()).then((result) => result.json());
};

export type EventData = {
  offerLabel?: string;
  title: string;
  date: string;
  isoDate?: string;
  sessionType: string;
  price: string;
  imageUrl: string;
  locationName: string;
  ctaUrl: string;
  badgeLabel?: string;
  showBadge?: boolean;
  badgeVariant?: string;
  ctaLabel?: string;
  icon?: string;
  description?: string;
  tableAvailability?: string[];
  filters?: any;
};

function eventsDataMapper({ data }: EventsResult, dateFormatter: Intl.DateTimeFormat): EventData[] {
  return data.map((element) => ({
    offerLabel: element.ticket_offer_desc,
    title: element.offer_desc,
    date: dateFormatter.format(parseISO(element.event_date)),
    isoDate: element.event_date,
    sessionType: element.session_type,
    price: element.price_from,
    imageUrl: element.image_link,
    locationName: (element.arena_name || element.px_space)!,
    ctaUrl: element.buy_link,
    moreInfoLink: element?.more_info_link,
    badgeLabel:
      element.avail_status === 'ALLOCATION EXHAUSTED'
        ? 'Pending Ticket Release'
        : element.avail_status,
    showBadge:
      element.avail_status === 'SELLING FAST' || element.avail_status === 'ALLOCATION EXHAUSTED',
    badgeVariant: element.avail_status === 'SELLING FAST' ? 'highlight' : 'secondary',
    ctaLabel: element.button_text,
    description: element.event_blurb,
    icon: element.icon,
    tableAvailability: element.table_availability || undefined
  }));
}

function paginatedEventsDataMapper(
  result: InfiniteData<EventsResult>,
  dateFormatter: Intl.DateTimeFormat
) {
  return {
    pageParams: result.pageParams,
    pages: result.pages.map((page) => ({
      data: eventsDataMapper(page, dateFormatter),
      pagination: page.pagination
    }))
  };
}

const placeholderCard: InputEventData = {
  event_date: '2022-01-17',
  event_time: '11:00 AM',
  arena_name: 'Ground Pass',
  session_type: 'All Day Sessions',
  ticket_offer_desc: 'Ticket Only',
  offer_desc: 'General Admission All Day Ground Pass',
  arena_sort_no: '4',
  ticket_offer_sort_no: '1',
  price_from: '49.00',
  avail_status: 'HIGH',
  onsale_status: 'ONSALE',
  button_text: 'BOOK TICKETS',
  buy_link: 'https://www.ticketmaster.com.au/event/TBCGroundPass.com.au',
  image_link:
    'https://res.cloudinary.com/dakp804eh/image/upload/v1630820029/airmodules/events-cards/arena.png',
  event_blurb: 'Liber ab celerabit. Frequentaretque resideresque. Creditusque in uerbera fidenatis.'
};

export function usePlaceholderEvents() {
  return usePaginatedPlaceholderEvents().pages[0];
}

export function usePaginatedPlaceholderEvents() {
  return useMemo<InfiniteData<EventsResult>>(
    () => ({
      pages: [{ data: new Array(8).fill(placeholderCard), pagination: { next: false } }],
      pageParams: [0]
    }),
    []
  );
}

const formatOptions = { month: 'short', day: 'numeric' } as const;

export function useEventsDataMapper() {
  const dateFormatter = useDateFormatter(formatOptions);
  return useCallback((result: EventsResult) => eventsDataMapper(result, dateFormatter), [
    dateFormatter
  ]);
}

export function usePaginatedEventsDataMapper() {
  const dateFormatter = useDateFormatter(formatOptions);
  return useCallback(
    (result: InfiniteData<EventsResult>) => paginatedEventsDataMapper(result, dateFormatter),
    [dateFormatter]
  );
}
